import { useEffect, useRef, useCallback } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * Creates a GSAP context bound to a component's lifecycle.
 * All animations created within the callback are auto-reverted on unmount.
 */
export function useGSAP<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const ctxRef = useRef<gsap.Context | null>(null);

  const animate = useCallback((fn: () => void) => {
    ctxRef.current?.revert();
    ctxRef.current = ref.current ? gsap.context(fn, ref) : gsap.context(fn);
  }, []);

  useEffect(() => {
    return () => {
      ctxRef.current?.revert();
    };
  }, []);

  return { ref, animate };
}

/**
 * Like useGSAP but also kills all ScrollTrigger instances on unmount.
 */
export function useScrollAnimation<T extends HTMLElement = HTMLDivElement>() {
  const { ref, animate } = useGSAP<T>();

  useEffect(() => {
    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  }, []);

  return { ref, animate };
}
